import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { linkPixel, ApiError, type LinkPixel } from '@/lib/api';
import {
  PixelVerificationDialog,
  type PixelVerificationResult,
} from './PixelVerificationDialog';

/**
 * Per-link Meta Pixel override block. Renders inside LinkDetail's edit
 * form. Two states:
 *
 *   1. Inherits org default (no row in `pixels` with this link_id) — the
 *      default. Shows a tiny "Using org default → /app/settings/pixel" hint.
 *   2. Custom — link has its own row in `pixels`. Shows the pixel ID + a
 *      write-only token field + test event code + LDU toggle, plus a
 *      "Use org default instead" link that deletes the override.
 *
 *  Free plan: read-only — shows the explainer and a small upgrade nudge.
 *
 *  Self-contained: maintains its own state + fires its own PUT/DELETE.
 *  Doesn't depend on the parent form's onSave — clicking "Save pixel"
 *  here only persists the pixel config, not the rest of the link form.
 *  Keeps cognitive load down and lets us add this without rewiring the
 *  big link form's save chain.
 */
export function LinkPixelOverride({ linkId }: { linkId: string }) {
  const [data, setData] = useState<LinkPixel | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  // Form state — synced from server on load + save.
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);
  const [testEventCode, setTestEventCode] = useState('');
  const [lduEu, setLduEu] = useState(false);
  // Local "I want to override" toggle for the inherit→custom transition.
  // When the server says override=false, we render the inherit hint; the
  // user clicks "Override for this link" to flip this on and reveal the
  // form.
  const [overrideMode, setOverrideMode] = useState(false);
  const [verifyResult, setVerifyResult] = useState<PixelVerificationResult | null>(null);

  function load() {
    linkPixel
      .get(linkId)
      .then((r) => {
        setData(r);
        setPixelId(r.pixelId || '');
        setTestEventCode(r.testEventCode || '');
        setLduEu(Boolean(r.lduEu));
        setAccessToken('');
        setTokenTouched(false);
        setOverrideMode(r.override);
      })
      .catch((e) => setErr((e as Error).message || 'Failed to load pixel.'));
  }

  useEffect(() => {
    load();
  }, [linkId]);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    setVerifyResult(null);
    try {
      const body: {
        pixelId: string;
        accessToken?: string;
        testEventCode?: string | null;
        lduEu?: boolean;
      } = {
        pixelId: pixelId.trim(),
        testEventCode: testEventCode.trim() || null,
        lduEu,
      };
      if (tokenTouched && accessToken) body.accessToken = accessToken;
      await linkPixel.put(linkId, body);
      setSaved(true);
      if (tokenTouched && accessToken) {
        setVerifyResult({ ok: true });
      }
      load();
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pixel_validation_failed') {
        const raw = (e.raw || {}) as {
          metaError?: string;
          metaCode?: number;
          httpStatus?: number;
        };
        setVerifyResult({
          ok: false,
          metaError: raw.metaError,
          metaCode: raw.metaCode,
          httpStatus: raw.httpStatus,
        });
      } else {
        const msg = (e as Error).message || '';
        if (msg.includes('plan_required'))
          setErr('Per-link pixel overrides are a Pro feature.');
        else if (msg.includes('access_token_required_on_first_save'))
          setErr('Paste an access token to enable this override.');
        else if (msg.includes('invalid_pixel_id'))
          setErr('Pixel ID must be numeric.');
        else if (msg.includes('pixel_id_required'))
          setErr('Pixel ID is required.');
        else setErr(msg || 'Save failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function removeOverride() {
    setBusy(true);
    setErr(null);
    try {
      await linkPixel.remove(linkId);
      load();
    } catch (e) {
      setErr((e as Error).message || 'Could not remove override.');
    } finally {
      setBusy(false);
    }
  }

  if (!data) return null;

  const canEdit = data.canEdit;

  // Free-plan summary card — no form, just an upgrade nudge.
  if (!canEdit) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-glass)] p-4 text-sm">
        <div className="font-semibold">Meta Pixel (advanced)</div>
        <p className="mt-1 text-[12px] text-[var(--color-muted-foreground)]">
          Server-side Conversions API tracking is a Pro feature. On Pro you
          can connect one pixel for your whole org (
          <RouterLink to="/app/settings/pixel" className="underline">
            org settings
          </RouterLink>
          ) and override per-link here if a specific link needs a different
          pixel.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[var(--color-border)] p-4">
      <div className="text-sm font-semibold">Meta Pixel (advanced)</div>
      <p className="mt-1 text-[12px] text-[var(--color-muted-foreground)]">
        Choose which pixel receives server-side PageView events for clicks
        on this link.
      </p>

      <div className="mt-3 space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            checked={!overrideMode}
            onChange={() => setOverrideMode(false)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Use organization default</span>
            <span className="block text-[12px] text-[var(--color-muted-foreground)]">
              Manage in{' '}
              <RouterLink to="/app/settings/pixel" className="underline">
                Settings → Meta Pixel
              </RouterLink>
              .
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            checked={overrideMode}
            onChange={() => setOverrideMode(true)}
            className="mt-0.5"
          />
          <span className="font-medium">Use a different pixel for this link</span>
        </label>
      </div>

      {overrideMode && (
        <div className="mt-4 space-y-3">
          <Field label="Pixel ID">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder="e.g. 681153731669782"
              className={inputCls}
            />
          </Field>

          <Field
            label="Access token"
            hint={
              data.hasToken && !tokenTouched
                ? 'Saved. Leave empty to keep; paste a new value to replace.'
                : undefined
            }
          >
            <input
              type="password"
              value={accessToken}
              onChange={(e) => {
                setAccessToken(e.target.value);
                setTokenTouched(true);
              }}
              placeholder={
                data.hasToken && !tokenTouched
                  ? '•••• already set'
                  : 'Paste the EAA… token'
              }
              autoComplete="new-password"
              className={inputCls}
            />
          </Field>

          <Field label="Test event code (optional)">
            <input
              type="text"
              value={testEventCode}
              onChange={(e) => setTestEventCode(e.target.value)}
              placeholder="TEST12345"
              className={inputCls}
            />
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={lduEu}
              onChange={(e) => setLduEu(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Limited Data Use for EU</span>
              <span className="block text-[12px] text-[var(--color-muted-foreground)]">
                Sends Meta's LDU flag for EU visitors.
              </span>
            </span>
          </label>

          {err && (
            <p className="rounded-md border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              {err}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-[var(--color-primary-foreground)] disabled:opacity-50"
            >
              {busy
                ? tokenTouched && accessToken
                  ? 'Verifying…'
                  : 'Saving…'
                : data.override
                ? 'Save pixel'
                : 'Set override'}
            </button>
            {data.override && (
              <button
                type="button"
                onClick={removeOverride}
                disabled={busy}
                className="text-xs text-[var(--color-destructive)] hover:underline disabled:opacity-50"
              >
                Remove override
              </button>
            )}
            {saved && (
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Saved.
              </span>
            )}
          </div>
        </div>
      )}

      <PixelVerificationDialog
        open={verifyResult !== null}
        result={verifyResult}
        onClose={() => setVerifyResult(null)}
      />
    </div>
  );
}

const inputCls =
  'mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)]';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] text-[var(--color-muted-foreground)]">
          {hint}
        </span>
      )}
    </label>
  );
}
